/**
 * `worstUrgencyColour` — combines two independent urgency-colour signals and
 * returns whichever is WORSE, so a single badge can be coloured by the
 * worse of two things without either one being silently hidden.
 *
 * Added for `components/Layout.js`'s `/department/orders` nav badge —
 * owner ruling, 2026-09: when the numeric admin-notification count is
 * showing, the badge must be coloured by the worse of the admin
 * notification colour and the tight-lead-time dot colour (which is
 * otherwise suppressed while the count is > 0), because a green "1" was
 * hiding red rows underneath it.
 *
 * 🔑 **Two call sites in this codebase spell the middle tier differently
 * and this function does NOT normalise either one.** `adminNotifColor`
 * (`GET /api/notifications/admin/urgency`) says `'yellow'`;
 * `urgencySummary.deptOrders.color` (`GET /api/notifications/urgency-summary`,
 * the same band `LeadTimeRisk.js#getLeadTimeBand` and `hireUrgency.js`
 * use elsewhere) says `'amber'`. They rank EQUAL. The function returns the
 * ORIGINAL token of whichever input wins — never a normalised third
 * spelling — so a caller comparing 'yellow' against a winning 'amber'
 * gets back 'amber' and must itself recognise both spellings when turning
 * the result into a Tailwind class (see the ternary at both Layout.js call
 * sites).
 *
 * Precedence: red > amber|yellow > green > anything else/unknown (ranked
 * as green — an unrecognised or missing colour must never win).
 *
 * @param {string|null|undefined} a
 * @param {string|null|undefined} b
 * @returns {string|null|undefined} `a` or `b`, whichever ranks worse; `a`
 *   on a tie (including both unranked/falsy, in which case `a` — even if
 *   `undefined` or `null` — is returned unchanged).
 */
const URGENCY_COLOUR_RANK = { red: 2, amber: 1, yellow: 1, green: 0 };

function urgencyColourRank(colour) {
  return URGENCY_COLOUR_RANK[colour] || 0;
}

export function worstUrgencyColour(a, b) {
  return urgencyColourRank(b) > urgencyColourRank(a) ? b : a;
}
